/**
 * Photo Crop Editor
 * Full-screen modal with fixed square viewfinder and zoomable/pannable image.
 */
import type { ProfilePhoto, CropData } from '../../types/api';

interface TransformState {
  scale: number;
  translateX: number;
  translateY: number;
}

export function openCropEditor(
  photo: ProfilePhoto,
  onSave: (crop: CropData) => Promise<void>
): void {
  const imgSrc = photo.files.large?.url || photo.files.original?.url || '';
  if (!imgSrc) return;

  // Create overlay
  const overlay = document.createElement('div');
  overlay.className = 'crop-editor-overlay';
  overlay.innerHTML = `
    <div class="crop-editor-toolbar">
      <button class="btn btn-secondary crop-editor-cancel">Cancel</button>
      <span class="crop-editor-title">Crop Photo</span>
      <button class="btn btn-primary crop-editor-save">Save</button>
    </div>
    <div class="crop-editor-viewport">
      <div class="crop-editor-frame"></div>
      <img class="crop-editor-image" src="${imgSrc}" draggable="false" />
    </div>
  `;
  document.body.appendChild(overlay);

  const img = overlay.querySelector('.crop-editor-image') as HTMLImageElement;
  const viewport = overlay.querySelector('.crop-editor-viewport') as HTMLElement;
  const frame = overlay.querySelector('.crop-editor-frame') as HTMLElement;
  const cancelBtn = overlay.querySelector('.crop-editor-cancel') as HTMLButtonElement;
  const saveBtn = overlay.querySelector('.crop-editor-save') as HTMLButtonElement;

  let state: TransformState = { scale: 1, translateX: 0, translateY: 0 };
  let frameSize = 0;
  let naturalW = 0;
  let naturalH = 0;
  let minScale = 1;

  function applyTransform(): void {
    img.style.transform = `translate(${state.translateX}px, ${state.translateY}px) scale(${state.scale})`;
  }

  function clampPan(): void {
    const scaledW = naturalW * state.scale;
    const scaledH = naturalH * state.scale;

    // Image edges must stay outside frame edges
    // Frame is centered in viewport; image origin is at viewport center
    const halfFrame = frameSize / 2;
    const maxTx = scaledW / 2 - halfFrame;
    const maxTy = scaledH / 2 - halfFrame;

    state.translateX = Math.max(-maxTx, Math.min(maxTx, state.translateX));
    state.translateY = Math.max(-maxTy, Math.min(maxTy, state.translateY));
  }

  function computeCropData(): CropData {
    // The frame is centered. Image is at center + translate, scaled.
    const scaledW = naturalW * state.scale;
    const scaledH = naturalH * state.scale;

    // Top-left of visible image in viewport coords relative to center
    const imgLeft = -scaledW / 2 + state.translateX;
    const imgTop = -scaledH / 2 + state.translateY;

    // Frame is centered: top-left is at (-frameSize/2, -frameSize/2)
    const frameLeft = -frameSize / 2;
    const frameTop = -frameSize / 2;

    // Crop region in image pixel coords
    const cropX = (frameLeft - imgLeft) / scaledW;
    const cropY = (frameTop - imgTop) / scaledH;
    const cropW = frameSize / scaledW;
    const cropH = frameSize / scaledH;

    return {
      x: Math.max(0, Math.min(1, cropX)),
      y: Math.max(0, Math.min(1, cropY)),
      w: Math.max(0.01, Math.min(1, cropW)),
      h: Math.max(0.01, Math.min(1, cropH)),
    };
  }

  function initFromCrop(crop: CropData | null): void {
    if (!crop || !naturalW || !naturalH) {
      // Default: min zoom, centered
      state.scale = minScale;
      state.translateX = 0;
      state.translateY = 0;
      return;
    }

    // Reverse-engineer scale and translate from crop data
    // cropW = frameSize / (naturalW * scale) => scale = frameSize / (cropW * naturalW)
    state.scale = frameSize / (crop.w * naturalW);
    state.scale = Math.max(minScale, Math.min(minScale * 4, state.scale));

    const scaledW = naturalW * state.scale;
    const scaledH = naturalH * state.scale;

    // cropX = (frameLeft - imgLeft) / scaledW
    // frameLeft = -frameSize/2
    // imgLeft = -scaledW/2 + tx
    // cropX = (-frameSize/2 - (-scaledW/2 + tx)) / scaledW
    // cropX = (scaledW/2 - frameSize/2 - tx) / scaledW
    // tx = scaledW/2 - frameSize/2 - cropX * scaledW
    state.translateX = scaledW / 2 - frameSize / 2 - crop.x * scaledW;
    state.translateY = scaledH / 2 - frameSize / 2 - crop.y * scaledH;

    clampPan();
  }

  img.onload = () => {
    naturalW = img.naturalWidth;
    naturalH = img.naturalHeight;

    // Frame size: ~80% of min(viewport width, viewport height)
    const vw = viewport.clientWidth;
    const vh = viewport.clientHeight;
    frameSize = Math.floor(Math.min(vw, vh) * 0.8);
    frame.style.width = `${frameSize}px`;
    frame.style.height = `${frameSize}px`;

    // Min scale: image must fill the frame
    const scaleToFillW = frameSize / naturalW;
    const scaleToFillH = frameSize / naturalH;
    minScale = Math.max(scaleToFillW, scaleToFillH);

    // Set image size to natural (scale via CSS transform)
    img.style.width = `${naturalW}px`;
    img.style.height = `${naturalH}px`;

    initFromCrop(photo.crop);
    applyTransform();
  };

  // --- Mouse drag ---
  let isDragging = false;
  let dragStartX = 0;
  let dragStartY = 0;
  let dragStartTx = 0;
  let dragStartTy = 0;

  viewport.addEventListener('mousedown', (e) => {
    e.preventDefault();
    isDragging = true;
    dragStartX = e.clientX;
    dragStartY = e.clientY;
    dragStartTx = state.translateX;
    dragStartTy = state.translateY;
  });

  const onMouseMove = (e: MouseEvent) => {
    if (!isDragging) return;
    state.translateX = dragStartTx + (e.clientX - dragStartX);
    state.translateY = dragStartTy + (e.clientY - dragStartY);
    clampPan();
    applyTransform();
  };

  const onMouseUp = () => { isDragging = false; };

  document.addEventListener('mousemove', onMouseMove);
  document.addEventListener('mouseup', onMouseUp);

  // --- Mouse wheel zoom ---
  viewport.addEventListener('wheel', (e) => {
    e.preventDefault();
    const zoomFactor = e.deltaY < 0 ? 1.1 : 0.9;
    const newScale = Math.max(minScale, Math.min(minScale * 4, state.scale * zoomFactor));

    // Zoom toward cursor position relative to viewport center
    const rect = viewport.getBoundingClientRect();
    const cx = e.clientX - rect.left - rect.width / 2;
    const cy = e.clientY - rect.top - rect.height / 2;

    const ratio = newScale / state.scale;
    state.translateX = cx - ratio * (cx - state.translateX);
    state.translateY = cy - ratio * (cy - state.translateY);
    state.scale = newScale;

    clampPan();
    applyTransform();
  }, { passive: false });

  // --- Touch pinch/pan ---
  let lastTouches: Touch[] = [];
  let lastPinchDist = 0;
  let lastPinchCenter = { x: 0, y: 0 };

  viewport.addEventListener('touchstart', (e) => {
    e.preventDefault();
    lastTouches = Array.from(e.touches);
    if (e.touches.length === 2) {
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      lastPinchDist = Math.sqrt(dx * dx + dy * dy);
      lastPinchCenter = {
        x: (e.touches[0].clientX + e.touches[1].clientX) / 2,
        y: (e.touches[0].clientY + e.touches[1].clientY) / 2,
      };
    }
  }, { passive: false });

  viewport.addEventListener('touchmove', (e) => {
    e.preventDefault();

    if (e.touches.length === 1 && lastTouches.length === 1) {
      // Pan
      const dx = e.touches[0].clientX - lastTouches[0].clientX;
      const dy = e.touches[0].clientY - lastTouches[0].clientY;
      state.translateX += dx;
      state.translateY += dy;
      clampPan();
      applyTransform();
    } else if (e.touches.length === 2) {
      // Pinch zoom
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      const dist = Math.sqrt(dx * dx + dy * dy);
      const center = {
        x: (e.touches[0].clientX + e.touches[1].clientX) / 2,
        y: (e.touches[0].clientY + e.touches[1].clientY) / 2,
      };

      if (lastPinchDist > 0) {
        const ratio = dist / lastPinchDist;
        const newScale = Math.max(minScale, Math.min(minScale * 4, state.scale * ratio));

        const rect = viewport.getBoundingClientRect();
        const cx = center.x - rect.left - rect.width / 2;
        const cy = center.y - rect.top - rect.height / 2;

        const actualRatio = newScale / state.scale;
        state.translateX = cx - actualRatio * (cx - state.translateX);
        state.translateY = cy - actualRatio * (cy - state.translateY);

        // Also pan with pinch center movement
        state.translateX += center.x - lastPinchCenter.x;
        state.translateY += center.y - lastPinchCenter.y;

        state.scale = newScale;
        clampPan();
        applyTransform();
      }

      lastPinchDist = dist;
      lastPinchCenter = center;
    }

    lastTouches = Array.from(e.touches);
  }, { passive: false });

  viewport.addEventListener('touchend', (e) => {
    lastTouches = Array.from(e.touches);
    if (e.touches.length < 2) {
      lastPinchDist = 0;
    }
  });

  // --- Buttons ---
  function cleanup(): void {
    document.removeEventListener('mousemove', onMouseMove);
    document.removeEventListener('mouseup', onMouseUp);
    overlay.remove();
  }

  cancelBtn.addEventListener('click', cleanup);

  saveBtn.addEventListener('click', async () => {
    saveBtn.disabled = true;
    saveBtn.textContent = 'Saving...';
    try {
      const crop = computeCropData();
      await onSave(crop);
      cleanup();
    } catch (err) {
      saveBtn.disabled = false;
      saveBtn.textContent = 'Save';
    }
  });

  // Close on overlay click (outside viewport area)
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) cleanup();
  });

  // Close on Escape
  const handleEscape = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      cleanup();
      document.removeEventListener('keydown', handleEscape);
    }
  };
  document.addEventListener('keydown', handleEscape);
}
